import { isTauri } from '@/tauri/utils';
import { setActiveWorkspace } from '@/tauri/workspace';
import { logger } from '@/utils/logger/logger';
import { bridgeErrorKind } from '@heidi/plugin-core';
import { sanitizeError } from '@heidi/runtime/logger';
import { useWorkspaceId } from '@heidi/workspace/data';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

type DesktopWorkspaceSync = { workspaceId: string | null; failed: boolean };

const desktopWorkspaceSyncAtom = atom<DesktopWorkspaceSync | null>(null);

/**
 * Mirrors the active workspace into Rust, whose own clients (sessions, Chronicle, uploads)
 * stamp `Heidi-Workspace-Id` outside axios. Sole writer of the Rust store besides the logout
 * clear; callers needing the workspace in Rust wait on {@link useIsActiveWorkspaceSyncedToDesktop}.
 */
export function useSyncActiveWorkspaceToDesktop(): void {
  const workspaceId = useWorkspaceId();
  const setWorkspaceSync = useSetAtom(desktopWorkspaceSyncAtom);

  useEffect(() => {
    if (!isTauri()) return;
    let current = true;
    // A failed push means Rust keeps stamping a stale (or no) workspace on
    // session/upload requests — silent misattribution, so it must be visible.
    setActiveWorkspace(workspaceId)
      .then(() => {
        if (current) setWorkspaceSync({ workspaceId, failed: false });
      })
      .catch((error) => {
        if (current) setWorkspaceSync({ workspaceId, failed: true });
        logger.warn({
          msg: '[workspace_header] Failed to push active workspace to Rust',
          workspaceId,
          errorKind: bridgeErrorKind(error),
          error: sanitizeError(error),
        });
      });
    return () => {
      current = false;
      // Cleared here, not at the next push: after A → B → A an old A
      // acknowledgement would report A synced while B's write is still queued.
      setWorkspaceSync(null);
    };
  }, [workspaceId, setWorkspaceSync]);
}

/** True once Rust has acknowledged the current, non-null workspace. */
export function useIsActiveWorkspaceSyncedToDesktop(): boolean {
  const workspaceId = useWorkspaceId();
  const sync = useAtomValue(desktopWorkspaceSyncAtom);
  return workspaceId !== null && sync?.workspaceId === workspaceId && !sync.failed;
}

/** True once the push for the current workspace has failed for good (retries exhausted). */
export function useHasActiveWorkspaceDesktopSyncFailed(): boolean {
  const workspaceId = useWorkspaceId();
  const sync = useAtomValue(desktopWorkspaceSyncAtom);
  return sync?.workspaceId === workspaceId && sync.failed;
}
